import React, { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import UserContext from 'context/user-context'
import GeneralSettings from './general'
import BrandingSettings from './branding'
import AiFeaturesSettings from './ai-features'
import Features from './features'
import UserSettings from './users'
import AccountGroups from '../account-groups'
import DocumentExemplars from '../document-exemplars'

// The Settings tab navigation + panels, without any app chrome. Each app shell
// wraps this in its own layout (analytics `Page`, access-management `Page`, or
// `DatumLayout`) so Settings is reachable from every app.
const SettingsPanels = () => {
  const navigate = useNavigate()
  const currentUser = useContext(UserContext)

  // Settings, AI Features and Attributes are analytics/SOD concepts — hidden
  // from users who have neither app. Branding is available to any user with
  // app access (analytics, SOD, or datum). The Users tab remains gated on
  // user-admin (server-enforced) regardless of app.
  const canManageOrgSettings = Boolean(
    currentUser?.has_analytics_app || currentUser?.has_sod_app
  )
  const canManageBranding = Boolean(
    currentUser?.has_analytics_app || currentUser?.has_sod_app || currentUser?.has_datum_app
  )
  const canManageUsers = Boolean(currentUser?.is_user_admin)
  // Account groups roll up account codes for reporting, so they follow the same
  // analytics/SOD rule as the other reporting-facing tabs.
  const canManageAccountGroups = canManageOrgSettings
  // Report blueprints feed the document-report agent, so this tab follows the
  // same beta gate as the Reports sidebar link.
  const canManageReportBlueprints = Boolean(canManageOrgSettings && currentUser?.is_beta)
  const defaultTab = canManageOrgSettings ? 'settings' : canManageBranding ? 'branding' : 'users'

  const [activeTab, setActiveTab] = useState(window.location.hash.substring(1) || defaultTab)

  useEffect(() => {
    if (window.location.hash === '#policies') {
      navigate('/policies', { replace: true })
    }
  }, [])

  useEffect(() => {
    const handleHashChange = () => {
      setActiveTab(window.location.hash.substring(1) || defaultTab)
    }
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  return (
    <Row>
      <Col sm={2}>
        <ul className="nav flex-column nav-pills fw-light rounded">
          {(canManageBranding || canManageUsers) && (
            <li>
              <h6 className="pb-1 ps-0 mt-3 dropdown-header">Organization</h6>
            </li>
          )}

          {canManageOrgSettings && (
            <li className="nav-item" onClick={() => setActiveTab('settings')}>
              <a
                className={`nav-link px-2 py-1 ${
                  activeTab === 'settings' ? 'active' : ''
                }`}
                href="#settings"
              >
                Settings
              </a>
            </li>
          )}

          {canManageBranding && (
            <li className="nav-item" onClick={() => setActiveTab('branding')}>
              <a
                className={`nav-link px-2 py-1 ${
                  activeTab === 'branding' ? 'active' : ''
                }`}
                href="#branding"
              >
                Branding
              </a>
            </li>
          )}

          {canManageOrgSettings && (
            <>
              <li className="nav-item" onClick={() => setActiveTab('ai-features')}>
                <a
                  className={`nav-link px-2 py-1 ${
                    activeTab === 'ai-features' ? 'active' : ''
                  }`}
                  href="#ai-features"
                >
                  AI Features
                </a>
              </li>

              <li className="nav-item" onClick={() => setActiveTab('attributes')}>
                <a
                  className={`nav-link px-2 py-1 ${
                    activeTab === 'attributes' ? 'active' : ''
                  }`}
                  href="#attributes"
                >
                  Attributes
                </a>
              </li>
            </>
          )}

          {canManageUsers && (
            <li className="nav-item" onClick={() => setActiveTab('users')}>
              <a
                className={`nav-link px-2 py-1 ${
                  activeTab === 'users' ? 'active' : ''
                }`}
                href="#users"
              >
                Users
              </a>
            </li>
          )}

          {(canManageAccountGroups || canManageReportBlueprints) && (
            <li>
              <h6 className="pb-1 ps-0 mt-3 dropdown-header">Reporting</h6>
            </li>
          )}

          {canManageAccountGroups && (
            <li className="nav-item" onClick={() => setActiveTab('account-groups')}>
              <a
                className={`nav-link px-2 py-1 ${
                  activeTab === 'account-groups' ? 'active' : ''
                }`}
                href="#account-groups"
              >
                Account Groups
              </a>
            </li>
          )}

          {canManageReportBlueprints && (
            <li className="nav-item" onClick={() => setActiveTab('report-blueprints')}>
              <a
                className={`nav-link px-2 py-1 ${
                  activeTab === 'report-blueprints' ? 'active' : ''
                }`}
                href="#report-blueprints"
              >
                Report blueprints
              </a>
            </li>
          )}

        </ul>
      </Col>
      <Col sm={9}>
        {canManageOrgSettings && activeTab === 'ai-features' && <AiFeaturesSettings />}
        {canManageOrgSettings && activeTab === 'attributes' && <Features />}
        {canManageOrgSettings && activeTab === 'settings' && <GeneralSettings />}
        {canManageBranding && activeTab === 'branding' && <BrandingSettings />}
        {/* The hash is user-controlled; UserSettings also self-guards and the
            API enforces is_user_admin regardless. */}
        {canManageUsers && activeTab === 'users' && <UserSettings />}
        {canManageAccountGroups && activeTab === 'account-groups' && <AccountGroups />}
        {canManageReportBlueprints && activeTab === 'report-blueprints' && <DocumentExemplars />}
      </Col>
    </Row>
  )
}

export default SettingsPanels
